import { EnumUtility } from '@codequality/vue-cli-base';

import { E不動産種類 } from '@/openapi/enum';
import { データListItemResponse, Cデータ保有不動産, 不動産価格 } from '@/openapi/user';

import AbstractデータService, { 帳票データ } from '@/classes/service/AbstractデータService';

export type 保有不動産EditItem = Cデータ保有不動産 & {
  データpk?: number;
  番号?: number;
};

export type 合計不動産価格 = {
  簿価?: number;
  評価額?: number;
  含み損益?: number;
};

export type 保有不動産ListItem = 保有不動産EditItem & {
  含み損益?: 不動産価格;
  合計?: 合計不動産価格;
};

type 保有不動産価格総計 = Required<Pick<保有不動産ListItem, '簿価' | '評価額' | '含み損益' | '合計'>>;

export type 保有不動産 = {
  list: 保有不動産ListItem[];
  total: 保有不動産価格総計;
};

export const type不動産価格Keys = EnumUtility.keys(E不動産種類) as (keyof 不動産価格)[];

export default abstract class 保有不動産Service extends AbstractデータService<保有不動産> {
  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * @returns
   */
  public abstract get get計算価格種類Keys(): readonly (keyof 合計不動産価格)[];

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(データs: データListItemResponse[]): 保有不動産 {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.入力分類Filter(データs);
    const addValue = (a?: number, b?: number, isNegativeB = false): number => {
      const va = a ?? 0;
      const vb = b === undefined ? 0 : isNegativeB ? 0 - b : b;
      return va + vb;
    };

    // 一覧
    const list: 保有不動産ListItem[] = filteredデータs.map((d) => {
      const cデータ保有不動産 = JSON.parse(d.cデータ) as Cデータ保有不動産;

      // 含み損益の計算(評価額－簿価)
      const v含み損益: 不動産価格 = {};
      for (const type不動産価格Key of type不動産価格Keys) {
        if (
          cデータ保有不動産.評価額?.[type不動産価格Key] === undefined &&
          cデータ保有不動産.簿価?.[type不動産価格Key] === undefined
        )
          continue;
        v含み損益[type不動産価格Key] = addValue(
          cデータ保有不動産.評価額?.[type不動産価格Key],
          cデータ保有不動産.簿価?.[type不動産価格Key],
          true
        );
      }

      // 合計の計算(土地＋建物＋借地権)
      const listItem = {
        ...cデータ保有不動産,
        含み損益: v含み損益,
      };
      const v合計: 合計不動産価格 = {};
      this.get計算価格種類Keys.forEach((価格種類key) => {
        type不動産価格Keys.forEach((type不動産価格Key) => {
          v合計[価格種類key] = addValue(v合計[価格種類key], listItem[価格種類key]?.[type不動産価格Key]);
        });
      });

      return {
        データpk: d.pk,
        番号: d.c番号,
        ...listItem,
        合計: v合計,
      };
    });

    // 合計行
    const total: 保有不動産価格総計 = Object.assign(
      {},
      ...[...this.get計算価格種類Keys, '合計'].map((key) => ({
        [key]: {},
      }))
    );

    list.forEach((listItem) => {
      this.get計算価格種類Keys.forEach((価格種類key) => {
        type不動産価格Keys.forEach((type不動産価格Key) => {
          total[価格種類key][type不動産価格Key] = addValue(
            total[価格種類key][type不動産価格Key],
            listItem[価格種類key]?.[type不動産価格Key]
          );
        });
        total.合計[価格種類key] = addValue(total.合計[価格種類key], listItem.合計?.[価格種類key]);
      });
    });

    // parse結果返却
    return {
      list,
      total,
    };
  }

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse帳票ListItemRequest(データs: データListItemResponse[]): 帳票データ {
    const raw = this.parse(データs);

    // テンプレート行設定
    const templateConfig = {
      list: {
        header: {
          main: 0,
          sub: 1,
        },
        row: {
          odd: 2,
          even: 3,
        },
        total: 4,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.list.header.main, templateConfig.list.header.sub];
    const values: unknown[][] = [[], []];

    const get帳票保有不動産価格総計 = (listItem: Partial<保有不動産価格総計>): (number | undefined)[] => {
      const arr: (number | undefined)[] = [];
      this.get計算価格種類Keys.forEach((価格種類key) => {
        type不動産価格Keys.forEach((type不動産価格Key) => {
          const val = listItem[価格種類key]?.[type不動産価格Key];
          arr.push(val === undefined ? undefined : this.getMoneyUnitValue(val));
        });
        const total = listItem.合計?.[価格種類key];
        arr.push(total === undefined ? undefined : this.getMoneyUnitValue(total));
      });
      return arr;
    };

    // 一覧整形
    raw.list.forEach((listItem, index) => {
      this.pushAlternateTemplateRow(templateRows, index, templateConfig.list.row.odd, templateConfig.list.row.even);
      values.push([
        listItem.番号,
        listItem.種類,
        listItem.所在,
        listItem.地番,
        listItem.名義,
        listItem.地目構造,
        listItem.地籍床面積,
        listItem.取得日,
        listItem.備考,
        ...get帳票保有不動産価格総計(listItem),
      ]);
    });

    // 合計整形
    templateRows.push(templateConfig.list.total);
    values.push([...this.templateCellValues(9), ...get帳票保有不動産価格総計(raw.total)]);

    return {
      templateRows,
      values,
    };
  }
}
